import { yupResolver } from '@hookform/resolvers/yup';
import { Typography } from '@mui/material';
import {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShopContent from 'components/_ShopContent';
import Loader from 'components/Loader';
import CategoryHeaderBar from 'containers/PosOrderDetailsPage/CategoryHeaderBar';
import OrderAmount from 'containers/PosOrderDetailsPage/OrderAmount';
import OrderHeader from 'containers/PosOrderDetailsPage/OrderHeader';
import OrderProductsList from 'containers/PosOrderDetailsPage/OrderProductsList';
import ProductsList from 'containers/PosOrderDetailsPage/ProductsList';
import Title from 'containers/PosOrderDetailsPage/Title';
import { SystemContext } from 'context/SystemContext';

import {
  addItemToOrder, getPOSOrderDetails, getProducts, updatePOSOrder,
} from 'services/POS';

import { POS_ROUTE } from 'constants/clientRoutes';
import { InventoryProductItem, InventorySearchPayload } from 'types/inventory.interface';
import { POSAddItemToOrderPayload, POSOrderDetails } from 'types/pos.interface';
import { handleApiErrors } from 'utils/errorUtils';
import { getClientDetailedInfo, getCompanyLocationsData } from 'utils/storageUtils';
import { UpdatePOSOrderFormData, updatePOSOrderSchema } from 'validation/updatePosOrderSchema';

import {
  HalfPageContainer,
  PageWrapper,
} from './index.styled';

const PosOrderDetailsPage = () => {
  const params = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [orderProductsLoading, setOrderProductsLoading] = useState(false);
  const [orderProducts, setOrderProducts] = useState<InventoryProductItem[]>([]);

  const [currentOrder, setCurrentOrder] = useState<POSOrderDetails | null>(null);
  const { list } = getCompanyLocationsData() || {};
  const { clientInfo } = useContext(SystemContext);
  const store = useMemo(() => list?.find(({ id }) => id === clientInfo?.id), [clientInfo, list]);

  const getOrderDefaultValues = (order: POSOrderDetails) => {
    if (!order) {
      return {};
    }

    return {
      id: order.id,
      customerId: order.customerId,
      locationId: order.locationId,
      useType: order.useType,
    } as Partial<POSOrderDetails>;
  };

  const navigate = useNavigate();

  const handleExitOrder = () => {
    navigate(POS_ROUTE);
  };

  const methods = useForm<UpdatePOSOrderFormData>({
    defaultValues: {},
    resolver: yupResolver(updatePOSOrderSchema),
  });

  const getOrderDetails = async () => {
    if (!params.orderId) {
      toast.error('No order ID provided');
      return;
    }

    try {
      setIsLoading(true);
      const { data } = await getPOSOrderDetails(params.orderId);
      setCurrentOrder(data);
      methods.reset(getOrderDefaultValues(data));
    } catch (err) {
      handleApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getOrderProducts = async () => {
    if (!params.orderId) {
      toast.error('No order ID provided');
      return;
    }

    try {
      const clientId = getClientDetailedInfo()?.clientInfo?.id;

      if (!clientId) {
        toast.error('No client ID provided');
        return;
      }

      setOrderProductsLoading(true);

      const payload: InventorySearchPayload = {
        start: 0,
        length: 1000,
        filter: {
          logic: 'AND',
          filters: [
            { field: 'HAS_AVAILABLE_INVENTORY', operator: 'EQ', value: true },
            { field: 'LOCATION_ID', operator: 'EQ', value: store?.locationId },
            { field: 'IS_PRODUCT', operator: 'EQ', value: true },
          ],
        },
      };
      const response = await getProducts(params.orderId, payload, clientId);

      setOrderProducts(response.data.list);
    } catch (err) {
      handleApiErrors(err);
    } finally {
      setOrderProductsLoading(false);
    }
  };

  const updateOrder = async (order: Partial<POSOrderDetails>) => {
    if (!params.orderId) {
      toast.error('No order ID provided');
      return;
    }

    try {
      const clientId = getClientDetailedInfo()?.clientInfo?.id;

      if (!clientId) {
        toast.error('No client ID provided');
        return;
      }

      await updatePOSOrder(params.orderId, order, clientId);
      getOrderDetails();
    } catch (err) {
      handleApiErrors(err);
    }
  };

  const onAddItemToOrderHandler = async (payload: POSAddItemToOrderPayload) => {
    try {
      const clientId = getClientDetailedInfo()?.clientInfo?.id;

      if (!clientId) {
        toast.error('No client ID provided');
        return;
      }

      if (!params.orderId) {
        toast.error('No order ID provided');
        return;
      }

      await addItemToOrder(params.orderId, clientId, payload);
      getOrderProducts();
      getOrderDetails();
    } catch (err) {
      handleApiErrors(err);
    }
  };

  useEffect(() => {
    if (params.orderId) {
      getOrderDetails();
      getOrderProducts();
    }
  }, [params.orderId]);

  if (isLoading) {
    return <Loader />;
  }

  if (!currentOrder) {
    return <Typography>No order found</Typography>;
  }

  return (
    <>
      <Title
        orderId={currentOrder.id}
        status={currentOrder.status}
        useType={currentOrder.useType}
        refreshOrder={getOrderDetails}
      />
      <ShopContent sx={{ flex: 1 }}>
        <PageWrapper>
          <FormProvider {...methods}>
            <HalfPageContainer>
              <CategoryHeaderBar
                order={currentOrder}
                hasMultipleUseTypes={Boolean(store?.adultUse && store?.medicalUse)}
                updateHandler={methods.handleSubmit(updateOrder)}
                onSuccessHandler={getOrderDetails}
              />
              <ProductsList
                products={orderProducts}
                loading={orderProductsLoading}
                onAddItemToOrderHandler={onAddItemToOrderHandler}
              />
            </HalfPageContainer>

            <HalfPageContainer>
              <OrderHeader
                orderId={currentOrder.id}
                status={currentOrder.status}
                onExitClick={handleExitOrder}
              />

              {currentOrder?.items && <OrderProductsList products={currentOrder.items} />}

              <OrderAmount items={currentOrder.items} />
            </HalfPageContainer>
          </FormProvider>
        </PageWrapper>
      </ShopContent>
    </>
  );
};

export default PosOrderDetailsPage;
